import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Text,
  Spinner
} from '@chakra-ui/react'
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from 'react-icons/fa'
import { LABEL } from '../../../constants'

const DropdownMultiSelect = ({
  fetchItems,
  onChange,
  initialSelected = [],
  displayField = 'name',
  extractValue = (item) => item,
  pageSize = 50,
  cif
}) => {
  const [allItems, setAllItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedValues, setSelectedValues] = useState(initialSelected)

  const loadItems = useCallback(async () => {
    setIsLoading(true)
    try {
      const resp = await fetchItems({ cif, searchTerm, size: pageSize })
      setAllItems(resp.content || [])
    } catch (e) {
      console.error('Error loading items', e)
    } finally {
      setIsLoading(false)
    }
  }, [fetchItems, searchTerm, pageSize, cif])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleOpen = () => {
    setSelectedValues(initialSelected)
  }

  const handleToggleValue = (value) => {
    setSelectedValues((previous) => {
      const exists = previous.includes(value)
      const updated = exists ? previous.filter((v) => v !== value) : [...previous, value]
      onChange && onChange(updated)
      return updated
    })
  }

  const visibleAll = allItems.filter(
    (item) => !selectedValues.includes(extractValue(item))
  )

  return (
    <Menu closeOnSelect={false} onOpen={handleOpen}>
      {({ isOpen }) => (
        <>
          <Flex align="center" w="100%">
            <MenuButton
              as={Button}
              variant="outline"
              size="sm"
              w="100%"
              textAlign="left"
              fontSize="sm"
              fontWeight="light"
              border="1px"
              borderColor="black"
              rightIcon={<Icon as={isOpen ? FaChevronUp : FaChevronDown} />}
            >
              {selectedValues.length > 0
                ? `${selectedValues.length} selected`
                : LABEL.DROPDOWN.select
              }
            </MenuButton>
            {selectedValues.length > 0 && (
              <IconButton
                icon={<FaTimes />}
                size="sm"
                ml={2}
                aria-label={LABEL.CLEAR_SELECTION?.label || 'Clear selection'}
                onClick={() => { setSelectedValues([]); onChange && onChange([]) }}
              />
            )}
          </Flex>

          <MenuList minW="320px" maxW="420px" p={0}>
            <Box px={3} py={2}>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" fontWeight="semibold">
                  Selected ({selectedValues.length})
                </Text>
                {selectedValues.length > 0 && (
                  <Button size="xs" variant="outline" onClick={() => { setSelectedValues([]); onChange && onChange([]) }}>
                    {LABEL.DELETE.label}
                  </Button>
                )}
              </Flex>

              {selectedValues.length === 0 ? (
                <Text fontSize="sm" color="gray.500">No items selected</Text>
              ) : (
                <Box maxH="120px" overflowY="auto">
                  {selectedValues.map((value) => {
                    const item = allItems.find((i) => extractValue(i) === value)
                    const label = item
                      ? (typeof item === 'string' ? item : item[displayField])
                      : value
                    return (
                      <Flex key={value} align="center" mb={1}>
                        <Checkbox isChecked onChange={() => handleToggleValue(value)}>
                          <Text fontSize="sm" ml={2}>{label}</Text>
                        </Checkbox>
                      </Flex>
                    )
                  })}
                </Box>
              )}
            </Box>

            <Divider />

            <Box px={3} py={2}>
              <Flex align="center" mb={2}>
                <Icon as={FaSearch} color="gray.500" mr={2} />
                <Input
                  placeholder={LABEL.SEARCH.label}
                  size="sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Flex>

              {isLoading ? (
                <Box textAlign="center" p={2}>
                  <Spinner size="sm" />
                </Box>
              ) : visibleAll.length === 0 ? (
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  {LABEL.EMPTY_FILTER.label}
                </Text>
              ) : (
                <Box maxH="200px" overflowY="auto">
                  {visibleAll.map((item) => {
                    const value = extractValue(item)
                    const label = typeof item === 'string' ? item : item[displayField]
                    return (
                      <Flex key={value} align="center" mb={1}>
                        <Checkbox onChange={() => handleToggleValue(value)}>
                          <Text fontSize="sm" ml={2}>{label}</Text>
                        </Checkbox>
                      </Flex>
                    )
                  })}
                </Box>
              )}
            </Box>
          </MenuList>
        </>
      )}
    </Menu>
  )
}

export default DropdownMultiSelect
